import React, {useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {useParams} from "react-router-dom";
import PageLayout from "../PageLayout";
import clubStore from "../../storage/ClubStore";
import appStore from "../../storage/AppStore";
import Select from "../../ui/Select";
import GameParticipantsEditor from "./GameParticipantsEditor";
import "../../common/game.css";

const GameParticipants = () => {
  const {id} = useParams();

  const game = clubStore.tournament.game;
  const myCompanyId = clubStore.club.id;

  const [teamOptions, setTeamOptions] = useState([]);
  const [playerOptions, setPlayerOptions] = useState([]);

  useEffect(() => {
    clubStore.tournament.getGame(id);
  }, [id]);

  const side = useMemo(() => {
    if (!game?.id) return null;

    if (game.company_id === myCompanyId) {
      return {
        role: "host",
        company_id: game.company_id,
        teamProp: "game_team_1_id",
        playerProp: "player_1_id",
        title: game.mode === "team" ? appStore.tr.team_members : `${appStore.tr.player} 1`,
      };
    }

    if (game.guest_company_id === myCompanyId) {
      return {
        role: "guest",
        company_id: game.guest_company_id,
        teamProp: "game_team_2_id",
        playerProp: "player_2_id",
        title: game.mode === "team"
          ? (appStore.tr.rival_team_members || appStore.tr.team_members)
          : `${appStore.tr.player} 2`,
      };
    }

    return null;
  }, [game?.id, game?.company_id, game?.guest_company_id, game?.mode, myCompanyId]);

  useEffect(() => {
    if (!side?.company_id) {
      setTeamOptions([]);
      setPlayerOptions([]);
      return;
    }

    if (game.mode === "team") {
      clubStore.tournament.getTeamsForCompany(side.company_id).then((ret) => {
        setTeamOptions(ret?.teams || []);
      });
      setPlayerOptions([]);
      return;
    }

    clubStore.tournament.getCompanyMembersDropdown(side.company_id).then((ret) => {
      setPlayerOptions(ret?.members || []);
    });
    setTeamOptions([]);
  }, [side?.company_id, game.mode]);

  if (!game?.id) return null;

  if (!side) {
    return (
      <PageLayout header={
        <div className="flex align-items-center gap-2 min-w-0 w-full">
          <i className="pi pi-users text-primary text-xl" aria-hidden="true"/>
          <h3 className="m-0 min-w-0">{appStore.tr.team_members}</h3>
        </div>
      }>
        <div className="col-12">
          <div className="game-page p-4 flex align-items-center gap-3">
            <i className="pi pi-lock text-color-secondary text-2xl" aria-hidden="true"/>
            <span className="font-medium">{appStore.tr.operation_is_denied || "Access denied"}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  const currentTeamId = game[side.teamProp];
  const isTeamGame = game.mode === "team";

  return (
    <PageLayout header={
      <div className="flex align-items-center gap-3 min-w-0 w-full">
        <i className="pi pi-users text-primary text-xl flex-shrink-0" aria-hidden="true"/>
        <h3 className="m-0 min-w-0 line-height-3">{game.name}</h3>
      </div>
    }>
      <div className="col-12">
        <div className="game-page p-3 md:p-4">
          <div className="flex align-items-center gap-3 flex-wrap mb-3">
            <div className="flex align-items-center justify-content-center surface-ground border-round-lg p-3 flex-shrink-0">
              <i className={`pi ${isTeamGame ? "pi-users" : "pi-user"} text-primary text-xl`} aria-hidden="true"/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-lg line-height-3">{side.title}</div>
              <div className="text-sm text-color-secondary mt-1">
                {isTeamGame
                  ? (appStore.tr.team_game || "Team Game")
                  : (appStore.tr.individual_game || "Individual Game")}
              </div>
            </div>
            <span className="badge badge-upcoming">
              <i className={`pi ${isTeamGame ? "pi-users" : "pi-user"}`} aria-hidden="true"/>
              {isTeamGame ? appStore.tr.team : appStore.tr.player}
            </span>
          </div>

          <div className="competition-detail-item">
            {isTeamGame ? (
              <Select store={game} prop={side.teamProp} label={appStore.tr.team} options={teamOptions}/>
            ) : (
              <Select store={game} prop={side.playerProp} label={appStore.tr.player} options={playerOptions}/>
            )}
          </div>
        </div>
      </div>

      {isTeamGame && !!currentTeamId && (
        <div className="col-12">
          <GameParticipantsEditor gameId={game.id} gameTeamId={currentTeamId}
            title={side.title} companyId={myCompanyId}/>
        </div>
      )}
    </PageLayout>
  );
};

export default observer(GameParticipants);
